import {
  faArrowLeft,
  faArrowRight,
  faBriefcase,
  faGlobe,
  faLocationDot
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useApplicant } from '~/api/queries/applicant'
import { useTrackPathViewedMutation } from '~/api/queries/pathViewed'
import { content as contentModalStyle } from '~/components/Global/Design/Modal/Modal.css'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { Toggle } from '~/components/Global/Interactions/Toggle/Toggle'
import { useCloseAction } from '~/components/Scopes/Actions/hooks/useCloseAction'
import {
  applicantModalAside,
  applicantModalMain,
  applicantModalMainHeader,
  applicantModalNavContainer,
  applicantModalNavContainerRight,
  applicantModalWrapper
} from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import { ApplicantModalActions } from '~/components/Scopes/Applicants/ApplicantModal/components/ApplicantModalActions'
import { ApplicantReports } from '~/components/Scopes/Applicants/ApplicantModal/components/ApplicantReports'
import { ApplicantModalHeader } from '~/domains/applicants/components/ApplicantModal/ApplicantModalHeader'
import { ApplicantModalLayout } from '~/domains/applicants/components/ApplicantModal/ApplicantModalLayout'
import { ApplicantNavDetails } from '~/domains/applicants/components/ApplicantModal/ApplicantNavDetails'
import { ApplicantHistory } from '~/domains/applicants/components/History/ApplicantHistory'
import { formatAddress } from '~/domains/common/utils'
import { useToggleConfigChange } from '~/domains/configuration/hooks/useToggleConfigChange'
import { useConfig } from '~/domains/configuration/queries/config'
import { useKeyboardNavigation } from '~/hooks/useKeyboardNavigation'
import { classicLink } from '~/styles/app.css'
import { formatName } from '~/utils/names'
import { useOffer } from '../../offers/queries/useOffer'
import { captureApplicationOpened } from '../analytics'
import { useMarkApplicationSeen } from '../mutations/markApplicationSeen'
import { useApplication } from '../queries/useApplication'
import type { ApplicationInfosResponse } from '../types'
import { ApplicationActions } from './ApplicationActions'
import { actionsFooter } from './ApplicationActions.css'
import { ApplicationAttachments } from './ApplicationAttachments'
import { applicationNoApplicantAside } from './ApplicationModal.css'
import { ApplicationModalErrorFallback } from './ApplicationModalErrorFallback'

type ApplicationModalProps = {
  defaultApplicationId: number
  collection?: ApplicationInfosResponse[]
  showOrder?: boolean
  onClose: () => void
}

export const ApplicationModal = ({
  defaultApplicationId,
  collection,
  showOrder = false,
  onClose
}: ApplicationModalProps) => {
  const posthog = usePostHog()
  const [applicationId, setApplicationId] = useState(defaultApplicationId)
  const [applicantId, setApplicantId] = useState<number | null>(null)
  const {
    data: application,
    isLoading,
    isFetching,
    isError
  } = useApplication({ id: applicationId })
  useMarkApplicationSeen(applicationId)
  const { data: offer } = useOffer(application?.offer?.id)
  const closeAction = useCloseAction('modal')
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { data: applicant } = useApplicant({ id: applicantId ?? undefined })
  const trackPathViewed = useTrackPathViewedMutation()

  const { data: autoAdvanceAfterDecision = false } = useConfig(
    'application.auto_advance_after_decision'
  )
  const handleAutoAdvanceChange = useToggleConfigChange(
    'application.auto_advance_after_decision'
  )

  useEffect(() => {
    setApplicantId(application?.applicant?.id ?? null)
  }, [application?.applicant?.id])

  useEffect(() => {
    captureApplicationOpened(posthog, applicationId)
  }, [posthog, applicationId])

  useEffect(() => {
    if (application?.order) {
      trackPathViewed.mutate({
        path: `/orders/${application.order.id}/applications/${applicationId}`
      })
    }
  }, [applicationId, application?.order, trackPathViewed.mutate])

  const onModalClose = () => {
    closeAction().then((confirm) => {
      if (confirm) {
        onClose()
      }
    })
  }

  const previousApplication =
    collection?.[collection?.findIndex(({ id }) => id === applicationId) - 1]
  const nextApplication =
    collection?.[collection?.findIndex(({ id }) => id === applicationId) + 1]

  const goToNextApplication = () => {
    if (nextApplication) {
      setApplicationId(nextApplication.id)
    }
  }

  useKeyboardNavigation({
    hasPrev: !!previousApplication,
    hasNext: !!nextApplication,
    onPrev: () =>
      previousApplication && setApplicationId(previousApplication.id),
    onNext: goToNextApplication
  })

  const isHeaderLoading = isLoading && !application
  const displayName = isHeaderLoading
    ? ''
    : formatName(applicant ?? application)
  const offerAddress = formatAddress(offer?.address)

  const completionDate = application?.completedAt
    ? new Date(application.completedAt)
    : null

  if (isError) {
    return (
      <ApplicationModalErrorFallback
        error={
          new Error(
            "Une erreur est survenue lors de l'affichage de cette candidature"
          )
        }
        resetErrorBoundary={() => {}}
        onClose={onClose}
      />
    )
  }

  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ApplicationModalErrorFallback {...props} onClose={onClose} />
      )}
      onError={(error, errorInfo) => {
        console.error('Application Modal Error:', error, errorInfo)
      }}
    >
      <ApplicantModalLayout onClose={onModalClose}>
        <section className={clsx(contentModalStyle, applicantModalWrapper)}>
          <main className={applicantModalMain}>
            <div className={applicantModalMainHeader}>
              <ApplicantModalHeader
                fullName={displayName}
                phone={
                  applicant?.phoneMobile ??
                  applicant?.phoneSecondary ??
                  application?.phone
                }
                applicantId={applicant?.id}
                isLoading={isHeaderLoading}
                isProfilePictureLoading={isHeaderLoading}
                applicantStatus={applicant?.status}
                role={
                  isHeaderLoading ? (
                    <Shimmer height={12} width={150} />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faBriefcase} />{' '}
                      {application?.offer?.id !== undefined ? (
                        <>
                          <a
                            href={`${legacyUrl}/admin/offres/voir/${application.offer.id}`}
                            className={classicLink}
                          >
                            {application.offer.title}
                          </a>
                          {offerAddress && (
                            <div>
                              <FontAwesomeIcon icon={faLocationDot} />{' '}
                              {offerAddress}
                            </div>
                          )}
                        </>
                      ) : (
                        <span>Candidature spontanée</span>
                      )}
                      {application?.source && (
                        <div>
                          <FontAwesomeIcon icon={faGlobe} />{' '}
                          {application.source}
                        </div>
                      )}
                    </>
                  )
                }
              >
                {(collection?.length ?? 0) > 1 && (
                  <>
                    {previousApplication && (
                      <ApplicantNavDetails
                        name={formatName(previousApplication)}
                        onClick={() =>
                          setApplicationId(previousApplication?.id)
                        }
                        className={applicantModalNavContainer}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </ApplicantNavDetails>
                    )}
                    {nextApplication && (
                      <ApplicantNavDetails
                        name={formatName(nextApplication)}
                        onClick={() => setApplicationId(nextApplication?.id)}
                        className={clsx(
                          applicantModalNavContainer,
                          applicantModalNavContainerRight
                        )}
                      >
                        <FontAwesomeIcon icon={faArrowRight} />
                      </ApplicantNavDetails>
                    )}
                  </>
                )}
              </ApplicantModalHeader>
            </div>
            <ApplicationAttachments applicationId={applicationId} />
          </main>
          <aside
            className={clsx(
              applicantModalAside,
              applicant ? null : applicationNoApplicantAside
            )}
          >
            {applicant && (
              <ApplicantModalActions
                applicantId={applicant.id}
                contextOrder={
                  application?.order?.id
                    ? {
                        id: application.order.id,
                        willChangeStatus: true
                      }
                    : undefined
                }
                hasEmail={!!applicant.email}
              />
            )}
            {application?.isCompleted && (
              <Alert
                variation={AlertVariation.Success}
                message=""
                onDismiss={false}
              >
                Cette candidature a été traitée
                {application.completedBy
                  ? ` par ${application.completedBy?.firstname} ${application.completedBy?.lastname}`
                  : ''}
                {completionDate && (
                  <Tooltip
                    text={completionDate?.toLocaleString()}
                    direction="top"
                    asChild
                  >
                    <span>
                      {' '}
                      {formatDistanceToNow(completionDate, {
                        addSuffix: true,
                        locale: fr
                      })}
                    </span>
                  </Tooltip>
                )}
              </Alert>
            )}
            {application?.isArchived && (
              <Alert
                variation={AlertVariation.Warning}
                message="Cette candidature a été archivée"
                onDismiss={false}
              />
            )}
            {application?.canProcess === false && (
              <Alert
                variation={AlertVariation.Danger}
                message="Vous n'avez pas les droits sur l'offre associée à cette candidature. Utilisez les flèches pour passer à la suivante."
                onDismiss={false}
              />
            )}
            {application &&
              !isFetching &&
              !application?.isCompleted &&
              !application?.isArchived &&
              application?.canProcess !== false && (
                <ApplicationActions
                  application={application}
                  applicantId={applicantId}
                  showOrder={showOrder}
                  onApplicantLinked={setApplicantId}
                  onProcessed={goToNextApplication}
                />
              )}
            {applicant && (
              <>
                <ApplicantReports
                  applicantId={applicant.id}
                  key={`applicant-reports-${applicant.id}`}
                />
                <ApplicantHistory
                  applicantId={applicant.id}
                  key={`applicant-history-${applicant.id}`}
                />
              </>
            )}

            {!application?.isCompleted && (
              <div className={actionsFooter}>
                <Toggle
                  defaultState={autoAdvanceAfterDecision ?? false}
                  onChange={handleAutoAdvanceChange}
                  inputSize="small"
                  label="Passer à la candidature suivante après décision"
                />
              </div>
            )}
          </aside>
        </section>
      </ApplicantModalLayout>
    </ErrorBoundary>
  )
}
